import React, { memo, useState, useRef, useEffect, useCallback } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Stack,
  Typography,
  Box,
  Divider,
} from "@mui/material";
import { CheckBox } from "@mui/icons-material";
import { formatAmount } from "../../../../../HelperFunctions/HelperFunctions";
import KeyboardArrowRightOutlinedIcon from "@mui/icons-material/KeyboardArrowRightOutlined";
import KeyboardArrowLeftOutlinedIcon from "@mui/icons-material/KeyboardArrowLeftOutlined";

const ChargebacksOrFeeTable = ({
  data,
  page: externalPage,
  setPage: setExternalPage,
}) => {
  const rowsPerPage = 40;

  const tableContainerRef = useRef(null);
  const [page, setPage] = useState(externalPage ?? 0);

  // Update external page state when internal page changes
  const handlePageChange = useCallback(
    (newPage) => {
      setPage(newPage);
      if (setExternalPage) {
        setExternalPage(newPage);
      }
    },
    [setExternalPage],
  );

  // Sync internal page state with external page prop
  useEffect(() => {
    if (externalPage !== undefined && externalPage !== page) {
      setPage(externalPage);
    }
  }, [externalPage, page]);

  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(data.length / rowsPerPage) - 1);
    const currentPage = externalPage !== undefined ? externalPage : page;
    const newPage = currentPage > maxPage ? maxPage : currentPage;
    if (newPage !== currentPage) {
      handlePageChange(newPage);
    }
  }, [data.length, rowsPerPage, externalPage, page, handlePageChange]);

  const columns = [
    { name: "Case ID", dataKey: "case_number", width: 250, isAmount: false },
    { name: "Date", dataKey: "date", width: 100, isAmount: false },
    { name: "Amount", dataKey: "amount", width: 100, isAmount: true },
    { name: "Category", dataKey: "category", width: 100, isAmount: false },
  ];

  const total = data.length;
  const totalPages = Math.ceil(total / rowsPerPage);
  const paginatedData = data.slice(
    page * rowsPerPage,
    (page + 1) * rowsPerPage,
  );

  const ValueCell = memo(({ value, isAmount }) => {
    if (!isAmount) return <span>{value ?? "-"}</span>;

    const displayValue = value !== null ? value : "-";
    return (
      <span
        style={{ display: "block", textAlign: "end" }}
        title={formatAmount(displayValue)}
      >
        ${formatAmount(displayValue)}
      </span>
    );
  });
  return (
    <>
      <TableContainer ref={tableContainerRef} sx={{ mt: 1, height: "285px" }}>
        <Table stickyHeader size="small">
          <TableHead sx={{ background: "#eee" }}>
            <TableRow>
              {columns.map((col) => (
                <TableCell
                  key={col.dataKey}
                  sx={{
                    backgroundColor: "#d7d7d7",
                    borderRight: "1px solid white",
                    fontSize: 13,
                    maxWidth: col.width,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {col.name}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length + 4}
                  align="center"
                  sx={{ fontSize: 14, color: "#999" }}
                >
                  No data available
                </TableCell>
              </TableRow>
            ) : (
              paginatedData.map((row, rowIndex) => (
                <TableRow key={rowIndex}>
                  {columns.map((col, colIndex) => {
                    return (
                      <TableCell
                        key={colIndex}
                        sx={{
                          fontSize: 12,
                          fontWeight: 500,
                          color: "text.secondary",
                          maxWidth: col.width,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          // p: 0
                        }}
                      >
                        {col.name !== "Chargebacks" ? (
                          <ValueCell
                            value={row[col.dataKey]}
                            isAmount={col.isAmount}
                          />
                        ) : (
                          <Chip
                            sx={{
                              width: "100%",
                              borderRadius: 1,
                              backgroundColor: "#f8ced3",
                              fontSize: "11px",
                              color: "#D9001B",
                              // px: 2,
                              height: "17px",
                              "& .MuiChip-icon": {
                                color: "#D9001B",
                              },
                            }}
                            label={
                              <span
                                title={formatAmount(row[col.dataKey]) ?? "-"}
                              >
                                {row[col.dataKey] !== undefined &&
                                row[col.dataKey] !== null
                                  ? formatAmount(row[col.dataKey])
                                  : "-"}
                              </span>
                            }
                            size="small"
                          />
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent={"space-between"}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            p: 1,
            gap: 1,
          }}
        >
          <Typography fontSize={"12px"}>Page</Typography>

          <button
            onClick={() => {
              if (page > 0) {
                handlePageChange(page - 1);
                setTimeout(() => {
                  if (tableContainerRef.current) {
                    tableContainerRef.current.scrollTop = 0;
                  }
                }, 0);
              }
            }}
            disabled={page === 0}
            style={{
              fontSize: "10px",
              cursor: page === 0 ? "not-allowed" : "pointer",

              backgroundColor: page >= totalPages - 1 ? "#f5f5f5" : "white",
            }}
          >
            <KeyboardArrowLeftOutlinedIcon fontSize="small" />
          </button>

          <input
            type="text"
            defaultValue={page + 1}
            key={page}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                const value = e.target.value.trim();
                let newPage;
                if (value === "" || value === "0") {
                  newPage = 0;
                } else {
                  const parsedPage = parseInt(value);
                  if (
                    !isNaN(parsedPage) &&
                    parsedPage >= 1 &&
                    parsedPage <= totalPages
                  ) {
                    newPage = parsedPage - 1;
                  } else if (parsedPage > totalPages) {
                    newPage = totalPages - 1;
                  } else {
                    newPage = 0;
                  }
                }
                handlePageChange(newPage);
                setTimeout(() => {
                  if (tableContainerRef.current) {
                    tableContainerRef.current.scrollTop = 0;
                  }
                }, 0);
                e.target.blur();
              }
            }}
            onFocus={(e) => {
              e.target.select();
            }}
            style={{
              width: "50px",
              padding: "4px",
              fontSize: "12px",
              textAlign: "center",
              border: "1px solid #ccc",
              borderRadius: "4px",
            }}
          />

          <button
            onClick={() => {
              if (page < totalPages - 1) {
                handlePageChange(page + 1);
                setTimeout(() => {
                  if (tableContainerRef.current) {
                    tableContainerRef.current.scrollTop = 0;
                  }
                }, 0);
              }
            }}
            disabled={page >= totalPages - 1}
            style={{
              // padding: '4px 8px',
              fontSize: "10px",
              cursor: page >= totalPages - 1 ? "not-allowed" : "pointer",

              backgroundColor: page >= totalPages - 1 ? "#f5f5f5" : "white",
            }}
          >
            <KeyboardArrowRightOutlinedIcon fontSize="small" />
          </button>

          <span style={{ fontSize: "14px" }}>of {totalPages}</span>
        </Box>
      </Stack>
    </>
  );
};

export default ChargebacksOrFeeTable;
